import { useCallback, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';

import debounce from 'lodash/debounce';

import { usePaymentLink, useRestaurant } from '@/hooks';
import { setTip } from '@/services';
import { useStore } from '@/store';
import { TipModeEnum } from '@/types';
import { calculateTip } from '@/utils';
import { useQlubRouter } from '@clubpay/customer-common-module/src/hook/router';
import { getDiningSession } from '@clubpay/customer-common-module/src/service/session/dsi';

export const useSyncTips = () => {
  const { data: restaurant } = useRestaurant();
  const { data: payment } = usePaymentLink();
  const selectedTip = useStore(state => state.selectedTip);
  const customTip = useStore(state => state.customTip);
  const { url } = useQlubRouter();

  const dsi = useCallback(() => {
    const diningSessionPayload = getDiningSession(url);
    return diningSessionPayload?.id || '';
  }, [url]);

  const tip = calculateTip({
    remaining: payment?.bill.remaining,
    selectedTip,
    customTip,
    tipMode: restaurant?.config.paymentLinkTipMode ?? TipModeEnum.Default,
    isTipEnabled: restaurant?.config.paymentLinkTipEnabled,
  });

  const mutation = useMutation({
    mutationFn: () => {
      return setTip({
        amount: tip.toFixed(2),
        diningSessionID: dsi(),
      });
    },
    onError: function () {
      // do nothing
    },
  });

  const debouncedTipHandler = useCallback(debounce(mutation.mutate, 1000), []);

  useEffect(() => {
    if (
      payment?.bill.remaining &&
      restaurant?.config.paymentLinkTipEnabled &&
      restaurant.config.paymentLinkTipOptions?.length
    ) {
      debouncedTipHandler();
    }
  }, [tip, payment?.bill.remaining, restaurant]);
};
